import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { actions as connectActions } from '../connect'
import testCampaignApi from './testCampaignApi'
import { isTerminalState, getPollingInterval, TestCampaignStatus } from './testCampaignUtils'

/**
 * @typedef {import('./testCampaignApi').TestCampaign} TestCampaign
 * @typedef {import('./testCampaignApi').TestCampaignPayload} TestCampaignPayload
 */

/**
 * @typedef {Object} SelectedAssets
 * @property {Object|null} adAccount
 * @property {Object|null} page
 * @property {Object|null} instagram
 * @property {Object|null} pixel
 */

/**
 * @typedef {Object} TestCampaignContextState
 * @property {TestCampaign|null} currentCampaign
 * @property {boolean} modalOpen
 * @property {string} modalView - 'selection' | 'progress' | 'success' | 'error'
 * @property {boolean} loading
 * @property {boolean} polling
 * @property {boolean} showAutoPrompt
 * @property {any} error
 * @property {SelectedAssets} selectedAssets
 */

const TestCampaignContext = createContext(null)

export function useTestCampaignContext() {
  const context = useContext(TestCampaignContext)
  if (!context) {
    throw new Error('useTestCampaignContext must be used within TestCampaignProvider')
  }
  return context
}

const initialSelectedAssets = {
  adAccount: null,
  page: null,
  instagram: null,
  pixel: null,
}

export function TestCampaignProvider({
  children,
  justConnected,
  assets,
  dispatch: connectDispatch,
}) {
  const [currentCampaign, setCurrentCampaign] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalView, setModalView] = useState('selection')
  const [loading, setLoading] = useState(false)
  const [polling, setPolling] = useState(false)
  const [showAutoPrompt, setShowAutoPrompt] = useState(false)
  const [error, setError] = useState(null)
  const [selectedAssets, setSelectedAssets] = useState(initialSelectedAssets)

  const pollIntervalRef = useRef(null)
  const pollStartTimeRef = useRef(null)

  useEffect(() => {
    if (justConnected && assets.length > 0) {
      setShowAutoPrompt(true)
      connectDispatch({ type: connectActions.SET_JUST_CONNECTED, payload: false })
    }
  }, [justConnected, assets, connectDispatch])

  useEffect(() => {
    checkForOngoingCampaign()
  }, [])

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
    }
  }, [])

  const checkForOngoingCampaign = useCallback(async () => {
    const { data, error } = await testCampaignApi.getOngoingCampaign()
    if (error) {
      console.error('Error checking for ongoing campaign:', error)
      return
    }

    if (data?.ongoing_campaign) {
      setCurrentCampaign(data.ongoing_campaign)
      if (!isTerminalState(data.ongoing_campaign.status)) {
        startPolling(data.ongoing_campaign.id)
      }
    }
  }, [])

  const startPolling = useCallback((campaignId) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
    }

    setPolling(true)
    pollStartTimeRef.current = Date.now()

    const poll = async () => {
      const { data, error } = await testCampaignApi.getTestCampaignById(campaignId)

      if (error) {
        console.error('Polling error:', error)
        stopPolling()
        return
      }

      if (data) {
        setCurrentCampaign(data)

        if (isTerminalState(data.status)) {
          stopPolling()
        } else {
          const elapsed = Date.now() - pollStartTimeRef.current
          const nextInterval = getPollingInterval(elapsed)

          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current)
          }
          pollIntervalRef.current = setInterval(poll, nextInterval)
        }
      }
    }

    poll()
  }, [])

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
    setPolling(false)
    pollStartTimeRef.current = null
  }, [])

  const createCampaign = useCallback(async () => {
    setLoading(true)
    setError(null)

    const payload = {
      ad_account_id: selectedAssets.adAccount.asset_id,
      profile_id: selectedAssets.page.asset_id,
    }

    if (selectedAssets.instagram) {
      payload.instagram_actor_id = selectedAssets.instagram.asset_id
    }

    if (selectedAssets.pixel) {
      payload.pixel_id = selectedAssets.pixel.asset_id
    }

    const { data, error } = await testCampaignApi.createTestCampaign(payload)

    setLoading(false)

    if (error) {
      setError(error)
      setModalView('error')
      return
    }

    if (data) {
      setCurrentCampaign(data)
      setModalView('progress')
      startPolling(data.id)
    }
  }, [selectedAssets, startPolling])

  const handleCloseAutoPrompt = useCallback(() => {
    setShowAutoPrompt(false)
  }, [])

  const handleAcceptAutoPrompt = useCallback(() => {
    setShowAutoPrompt(false)
    setModalOpen(true)
    setModalView('selection')
  }, [])

  const openModal = useCallback(() => {
    setError(null)
    if (currentCampaign && !isTerminalState(currentCampaign.status)) {
      setModalView('progress')
    } else {
      setModalView('selection')
      setSelectedAssets(initialSelectedAssets)
    }
    setModalOpen(true)
  }, [currentCampaign])

  const closeModal = useCallback(() => {
    setModalOpen(false)
    setError(null)
    if (currentCampaign && isTerminalState(currentCampaign.status)) {
      setModalView('selection')
      setSelectedAssets(initialSelectedAssets)
    }
  }, [currentCampaign])

  const selectAsset = useCallback((assetType, asset) => {
    setSelectedAssets((prev) => ({
      ...prev,
      [assetType]: asset,
    }))
  }, [])

  const clearSelection = useCallback(() => {
    setSelectedAssets(initialSelectedAssets)
  }, [])

  const retry = useCallback(() => {
    const campaign = currentCampaign
    if (campaign && assets.length > 0) {
      const adAccount = assets.find((a) => a.asset_id === campaign.ad_account_id)
      const page = assets.find(
        (a) => a.asset_id === campaign.assets?.profile_id || campaign.profile_id,
      )
      const instagram =
        campaign.assets?.instagram_user_id || campaign.instagram_actor_id
          ? assets.find(
            (a) =>
              a.asset_id === (campaign.assets?.instagram_user_id || campaign.instagram_actor_id),
          )
          : null
      const pixel =
        campaign.assets?.pixel_id || campaign.pixel_id
          ? assets.find((a) => a.asset_id === (campaign.assets?.pixel_id || campaign.pixel_id))
          : null

      setSelectedAssets({
        adAccount: adAccount || null,
        page: page || null,
        instagram: instagram || null,
        pixel: pixel || null,
      })
    }

    setModalView('selection')
    setError(null)
  }, [currentCampaign, assets])

  const viewPreviousCampaign = useCallback(() => {
    const campaign = currentCampaign
    if (campaign) {
      if (campaign.status === TestCampaignStatus.SUCCESS) {
        setModalView('success')
      } else if (campaign.status === TestCampaignStatus.FAILURE) {
        setModalView('error')
      }
    }
  }, [currentCampaign])

  useEffect(() => {
    if (currentCampaign && modalOpen) {
      if (currentCampaign.status === TestCampaignStatus.SUCCESS && modalView === 'progress') {
        setModalView('success')
      } else if (
        currentCampaign.status === TestCampaignStatus.FAILURE &&
        modalView === 'progress'
      ) {
        setModalView('error')
      } else if (
        currentCampaign.status === TestCampaignStatus.IN_PROGRESS ||
        currentCampaign.status === TestCampaignStatus.PENDING
      ) {
        setModalView('progress')
      }
    }
  }, [currentCampaign, modalOpen, modalView])

  const value = {
    currentCampaign,
    modalOpen,
    modalView,
    loading,
    polling,
    showAutoPrompt,
    error,
    selectedAssets,
    handleCloseAutoPrompt,
    handleAcceptAutoPrompt,
    openModal,
    closeModal,
    selectAsset,
    clearSelection,
    createCampaign,
    retry,
    viewPreviousCampaign,
  }

  return <TestCampaignContext.Provider value={value}>{children}</TestCampaignContext.Provider>
}

TestCampaignProvider.propTypes = {
  children: PropTypes.node.isRequired,
  justConnected: PropTypes.bool.isRequired,
  assets: PropTypes.array.isRequired,
  dispatch: PropTypes.func.isRequired,
}
